import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getRepFormData, getSteps } from "./selectors";
import * as yup from "yup";
import { RepInfoSteps } from "constants/enums";

function usePassengerValidation(targetStepKey) {
  const steps = useSelector(getSteps);
  const formData = useSelector(getRepFormData);
  const [pending, setPending] = useState(false);
  const [valid, setValid] = useState(false);
  const [errors, setErrors] = useState([]);

  const schema = useMemo(() => {
    const schemaShape = steps
      .filter((x) => !targetStepKey || x.key == targetStepKey)
      .reduce((acc, x) => {
        switch (x.key) {
          case RepInfoSteps.Personal:
            return {
              ...acc,
              [RepInfoSteps.Personal]: yup.object().shape({
                title: yup.string().nullable().required("Title"),
                firstName: yup.string().nullable().required("First Name"),
                lastName: yup.string().nullable().required("Last Name"),
                gender: yup.string().nullable().required("Gender"),
                dob: yup
                  .date()
                  .nullable()
                  .test("Date of Birth", "Date of Birth", (value) => {
                    return value != null && value < new Date();
                  }),
                mobile: yup.string().nullable().required("Mobile"),
              }),
            };
          case RepInfoSteps.Passport:
            return {
              ...acc,
              [RepInfoSteps.Passport]: yup
                .object()
                .nullable()
                .shape({
                  passportNum: yup
                    .string()
                    .nullable()
                    .required("Passport Number"),
                  passportDateOfIssue: yup
                    .string()
                    .nullable()
                    .required("Passport Date of Issue"),
                  passportExpiryDate: yup
                    .string()
                    .nullable()
                    .required("Passport Expiry Date"),
                  nationality: yup.string().nullable().required("Nationality"),
                  issueCountry: yup
                    .string()
                    .nullable()
                    .required("Country of Issue/Code"),
                }),
            };
          case RepInfoSteps.Medical:
            return {
              ...acc,
              [RepInfoSteps.Medical]: yup
                .object()
                .nullable()
                .shape({
                  hasMedicalRequirements: yup
                    .bool()
                    .nullable()
                    .required("Do you have medical/dietary requirements?"),
                  epipenCarrier: yup
                    .bool()
                    .nullable()
                    .when("hasMedicalRequirements", {
                      is: (x) => x == true,
                      then: yup
                        .bool()
                        .nullable()
                        .required("Do you carry an epipen?"),
                    }),
                }),
            };
          case RepInfoSteps.Additional:
            return {
              ...acc,
              [RepInfoSteps.Additional]: yup
                .object()
                .nullable()
                .shape({
                  nextOfKin: yup.string().nullable().required("Next of Kin"),
                  nextOfKinContact: yup
                    .string()
                    .nullable()
                    .required("Next of Kin Contact Number"),
                  nextOfKinRelationship: yup
                    .string()
                    .nullable()
                    .required("Next of Kin Relationship"),
                  drivingLicence: yup
                    .string()
                    .nullable()
                    .required("Driving Licence"),
                  uniformSize: yup.string().nullable().required("Uniform Size"),
                }),
            };

          default:
            return acc;
        }
      }, {});
    return yup.object().shape(schemaShape);
  }, [steps]);

  useEffect(() => {
    setPending(true);

    Promise.allSettled([
      schema.isValid(formData),
      schema.validate(formData, { abortEarly: false }),
    ])
      .then(([validResult, errorsResult]) => {
        setValid(validResult.status == "fulfilled" ? validResult.value : false);
        setErrors(
          errorsResult.status == "rejected"
            ? errorsResult.reason?.errors || []
            : []
        );
      })
      .catch(() => {
        setValid(false);
        setErrors([]);
      })
      .finally(() => setPending(false));
  }, [schema, formData]);

  return { pending, valid, errors };
}

export default usePassengerValidation;
